import { supabase } from "@/integrations/supabase/client";
import { API_ROUTES } from "@/api/routes";
import { API_URL } from "@/services/api";

/**
 * First-party analytics tracker (replaces PostHog, Mixpanel and GTM).
 *
 * Sends pageviews, page-leave durations, link/button clicks on public pages
 * and named conversions to POST /analytics/events. No cookies or local
 * storage: the API identifies signed-in users by their session token and
 * counts anonymous visitors with a daily-rotating server-side hash.
 */

export type ConversionEvent =
  | "cta_click_call"
  | "cta_click_whatsapp"
  | "inquiry_submitted"
  | "scroll_to_services";

interface ClickElement {
  tag: "a" | "button" | "other";
  text?: string;
  href?: string;
}

interface TrackedEvent {
  name: string;
  path: string;
  referrer?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  durationMs?: number;
  element?: ClickElement;
}

const BATCH_SIZE = 20;
const FLUSH_MS = 5_000;
/** Signed-in app areas: pageviews only, never click text (it can hold customer names). */
const PRIVATE_AREAS = /^\/(dashboard|super-admin|portal)(\/|$)/;

let queue: TrackedEvent[] = [];
let timer: ReturnType<typeof setTimeout> | undefined;
let current: { path: string; since: number } | undefined;
let started = false;
/** Kept in step with the session so a flush on page hide never waits on a promise. */
let accessToken: string | undefined;

/** Off for prerendering, test browsers and builds without an API. */
export function isAnalyticsEnabled(): boolean {
  return typeof window !== "undefined" && !!API_URL && !navigator.webdriver;
}

/** Installs the session, click and page-hide listeners once. */
export function startAnalytics(): void {
  if (started || !isAnalyticsEnabled()) return;
  started = true;
  void supabase.auth.getSession().then(({ data }) => {
    accessToken = data.session?.access_token;
  });
  supabase.auth.onAuthStateChange((_event, session) => {
    accessToken = session?.access_token;
  });
  document.addEventListener("click", onClick, { capture: true, passive: true });
  window.addEventListener("pagehide", onPageHide);
  document.addEventListener("visibilitychange", onVisibilityChange);
}

function onPageHide(): void {
  leaveCurrentPage();
  flush();
}

function onVisibilityChange(): void {
  if (document.visibilityState === "hidden") flush();
}

/** Record a pageview; closes the previous page with its time on page. */
export function trackPageview(path: string, search = ""): void {
  if (!isAnalyticsEnabled() || path === current?.path) return;
  const first = !current;
  leaveCurrentPage();
  const params = new URLSearchParams(search);
  enqueue({
    name: "$pageview",
    path,
    referrer: first ? document.referrer || undefined : undefined,
    utmSource: params.get("utm_source") || undefined,
    utmMedium: params.get("utm_medium") || undefined,
    utmCampaign: params.get("utm_campaign") || undefined,
  });
  current = { path, since: Date.now() };
}

/** Record a named conversion on the current page. */
export function trackConversion(name: ConversionEvent): void {
  if (!isAnalyticsEnabled()) return;
  enqueue({ name, path: window.location.pathname });
}

function leaveCurrentPage(): void {
  if (!current) return;
  enqueue({ name: "$pageleave", path: current.path, durationMs: Math.min(Date.now() - current.since, 86_400_000) });
  current = undefined;
}

function onClick(e: MouseEvent): void {
  const path = window.location.pathname;
  if (PRIVATE_AREAS.test(path) || !(e.target instanceof Element)) return;
  const el = e.target.closest("a, button, [role='button'], [role='link']");
  if (!el) return;
  const tag = el.tagName === "A" ? "a" : el.tagName === "BUTTON" ? "button" : "other";
  const text = (el.getAttribute("aria-label") || el.textContent || "").replace(/\s+/g, " ").trim().slice(0, 120);
  const href = el.tagName === "A" ? el.getAttribute("href")?.split(/[?#]/)[0].slice(0, 512) : undefined;
  enqueue({ name: "$click", path, element: { tag, text: text || undefined, href: href || undefined } });
}

function enqueue(event: TrackedEvent): void {
  queue.push(event);
  if (queue.length >= BATCH_SIZE) flush();
  else timer ??= setTimeout(flush, FLUSH_MS);
}

function flush(): void {
  clearTimeout(timer);
  timer = undefined;
  while (queue.length) {
    const events = queue.splice(0, 25);
    fetch(`${API_URL}${API_ROUTES.analytics.events()}`, {
      method: "POST",
      keepalive: true,
      headers: {
        "Content-Type": "application/json",
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      },
      body: JSON.stringify({ events }),
    }).catch(() => {
      // Analytics never interrupts the page.
    });
  }
}

/** Test seam: forget all tracker state. */
export function resetAnalyticsForTests(): void {
  clearTimeout(timer);
  queue = [];
  timer = undefined;
  current = undefined;
  started = false;
  accessToken = undefined;
}
